import { PUBLISHER_BULK_MAX_IDS, type PublisherWithStats } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import { BadgeCheckIcon } from 'lucide-react';
import { usePublishersContent } from '../../content/sections/publishers.js';
import { PublisherActions, type StatusAction } from './publisher-actions.js';
import { PublisherStatsCell } from './publisher-stats-cell.js';
import { statusLabel, statusTone } from './publisher-status-map.js';

/* Tabela do painel de publishers (ADR-0471) sobre o primitive `Table` de @planuze/ui (ADR-0700):
   seleção em lote + stats + ações por linha. `displayName` é drill-down para `/publishers/:id`
   (ADR-0476). Empty diferenciado (§6): busca/status sem resultados ≠ nenhum publisher. */

export function PublishersTable({
  publishers,
  canManage,
  isSuperAdmin,
  selectedIds,
  busyId,
  hasFilters,
  onClearFilters,
  onToggle,
  onToggleAll,
  onVerify,
  onRequestAction,
}: {
  readonly publishers: readonly PublisherWithStats[];
  readonly canManage: boolean;
  readonly isSuperAdmin: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly busyId: string | null;
  /** Busca ou filtro de status ativo — diferencia "sem resultados" de "sem publishers" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
  readonly onVerify: (id: string) => void;
  readonly onRequestAction: (publisher: PublisherWithStats, action: StatusAction) => void;
}) {
  const copy = usePublishersContent();
  if (publishers.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<BadgeCheckIcon className="size-6" />}
        title={copy.table.noResultsTitle}
        description={copy.table.noResultsDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.table.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<BadgeCheckIcon className="size-6" />}
        title={copy.table.emptyTitle}
        description={copy.table.emptyDescription}
      />
    );
  }
  const selectedVisibleCount = publishers.filter((publisher) =>
    selectedIds.has(publisher.id),
  ).length;
  const selectionTarget = Math.min(publishers.length, PUBLISHER_BULK_MAX_IDS);
  const allSelected = selectionTarget > 0 && selectedVisibleCount >= selectionTarget;
  const selectionFull = selectedVisibleCount >= PUBLISHER_BULK_MAX_IDS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canManage && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(c) => onToggleAll(c === true)}
                aria-label={copy.table.selectAllAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.table.headers.publisher}</TableHead>
          <TableHead>{copy.table.headers.status}</TableHead>
          <TableHead>{copy.table.headers.keys}</TableHead>
          <TableHead>{copy.table.headers.moderation}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {publishers.map((publisher) => {
          const signed = publisher.keys.filter((k) => k.rootSigned && !k.revoked).length;
          const pendingKeys = publisher.keys.filter((k) => !k.rootSigned && !k.revoked).length;
          return (
            <TableRow key={publisher.id}>
              {canManage && (
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(publisher.id)}
                    disabled={selectionFull && !selectedIds.has(publisher.id)}
                    onCheckedChange={() => onToggle(publisher.id)}
                    aria-label={copy.table.selectRowAriaLabel(publisher.slug)}
                  />
                </TableCell>
              )}
              <TableCell>
                <Link
                  to="/publishers/$id"
                  params={{ id: publisher.id }}
                  className="font-medium text-foreground transition-colors hover:underline"
                >
                  {publisher.displayName}
                </Link>
                <div className="font-mono text-xs text-muted-foreground">{publisher.slug}</div>
              </TableCell>
              <TableCell>
                <StatusBadge variant={statusTone(publisher.status)} dot>
                  {statusLabel(publisher.status, copy.statusLabels)}
                </StatusBadge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {copy.table.keysSummary(signed, pendingKeys)}
              </TableCell>
              <TableCell>
                <PublisherStatsCell stats={publisher.stats} />
              </TableCell>
              <TableCell className="text-right">
                <PublisherActions
                  publisher={publisher}
                  canManage={canManage}
                  isSuperAdmin={isSuperAdmin}
                  busy={busyId !== null}
                  onVerify={onVerify}
                  onRequestAction={onRequestAction}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
